import React, { useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  AlertsBellIconWrapper,
  AlertsBellTitleButton,
  AlertsBellStyledText,
  AlertsBellWrapper,
} from './AlertsBell.styled'
import { ExclamationMark } from 'Assets/icons/ExclamationMark'
import { InvitationAcceptanceModal } from 'Features/pricing/components/PendingInvitations/components/InvitationAcceptanceModal'
import { UserMenuOption, UserMenuTrigger } from 'Components/Elements/UserMenu/UserMenu.styled'
import { useGetUserAccounts } from 'Features/AccountSelector/api/getUserAccounts'
import { useAcceptPendingInvitation } from 'Features/pricing/api/useAcceptPendingInvitation'
import useUserStore from 'Stores/user'
import { Bell } from 'Assets/icons/Bell'
import { Popover, StyledText } from '@clickcease/cc-client-components-lib'
import { UserMenuStyled } from 'Features/AccountSelector/AccountSelector.styled'

interface AlertsBellProps {
  onInviteAccepted?: () => void
}

export const AlertsBell: React.FC<AlertsBellProps> = ({ onInviteAccepted }) => {
  const { t } = useTranslation('general')
  const { t: tPricing } = useTranslation('pricing')
  const { userId } = useUserStore()
  const { data: accounts } = useGetUserAccounts()
  const pendingInvitations = accounts?.pendingInvitations || []
  const hasPendingInvitation = pendingInvitations.length > 0

  // Add more conditions with || in the future
  const hasAlerts = hasPendingInvitation

  const [isAcceptModalOpen, setIsAcceptModalOpen] = useState(false)

  const { mutate: acceptPendingInvitation } = useAcceptPendingInvitation({
    onSuccess: () => {
      onInviteAccepted?.()
    },
  })

  const handleAcceptInvite = useCallback(
    (selectedInvite: number[]) => {
      acceptPendingInvitation({ userId, accounts: selectedInvite })
    },
    [userId, acceptPendingInvitation]
  )

  const handleCloseAcceptModal = useCallback(() => {
    setIsAcceptModalOpen(false)
  }, [])

  const handleOpenAcceptModal = useCallback(() => {
    setIsAcceptModalOpen(true)
  }, [])

  const popoverContent = (
    <UserMenuStyled>
      {hasAlerts ? (
        <>
          {hasPendingInvitation && (
            <UserMenuOption>
              <AlertsBellTitleButton onClick={handleOpenAcceptModal}>
                <AlertsBellStyledText>
                  {pendingInvitations.length === 1
                    ? tPricing('pending_invitations.single_pending_invite')
                    : tPricing('pending_invitations.multiple_pending_invites')}
                </AlertsBellStyledText>
              </AlertsBellTitleButton>
            </UserMenuOption>
          )}
        </>
      ) : (
        <UserMenuOption centerText>
          <StyledText variant="baseLight">{t('top_nav.no_messages')}</StyledText>
        </UserMenuOption>
      )}
    </UserMenuStyled>
  )

  return hasAlerts ? (
    <AlertsBellWrapper>
      <Popover
        style={{ zIndex: 1 }}
        trigger={
          <UserMenuTrigger noMarginSVG vibrateAnimation={hasAlerts}>
            <Bell />
            <AlertsBellIconWrapper>
              <ExclamationMark height={20} />
            </AlertsBellIconWrapper>
          </UserMenuTrigger>
        }
        content={popoverContent}
      />

      <InvitationAcceptanceModal
        pendingInvitations={pendingInvitations}
        onAccept={handleAcceptInvite}
        isOpen={isAcceptModalOpen}
        onClose={handleCloseAcceptModal}
      />
    </AlertsBellWrapper>
  ) : (
    <AlertsBellWrapper>
      <Popover
        style={{ zIndex: 1 }}
        trigger={
          <UserMenuTrigger noMarginSVG vibrateAnimation={false}>
            <Bell />
          </UserMenuTrigger>
        }
        content={popoverContent}
      />
    </AlertsBellWrapper>
  )
}
